import {Button, Modal, Textarea, TextInput} from "@mantine/core";
import "ace-builds/src-noconflict/mode-javascript";

import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/mode-mysql";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";
import './smartTriggerProcessorConfigurationModal.css'
import AceEditor from "react-ace";
import {LanguageCard} from "./languageCard";
import {ChangeEvent, useEffect, useState} from "react";
import {v4 as uuidv4} from 'uuid';
import {
    ScriptLanguage,
    SmartTriggerProcessorData,
    SmartTriggerProcessorNodeConfiguration,
    SmartTriggerScriptProcessorData
} from "../../types";
import {useSmartTriggerRepository} from "../../context/smartTriggerRepositoryProvider";
import {StringUtils} from "../../../../utils/stringUtils";
import {Bug, CircleX, DeviceFloppy} from "tabler-icons-react";
import {ObjectUtils} from "../../../../utils/objectUtils";
import {useSmartTriggerConfiguration} from "../../context/smartTriggerConfigurationProvider";
import {findProcessorNode} from "../../service/smartTriggerService";

export type SmartTriggerProcessorConfigurationModalProps = {
    isOpened: boolean,
    onClose: () => void,
    action: SmartTriggerProcessorConfigurationAction
}

type ProcessorUpdateAction = {
    type: "update",
    processorId: string
}

type NewProcessorAction = {
    type: 'new',
    previous: string,
    next: string
}

type ProcessRepositoryPullAction = {
    type: "pull",
    processorRepositoryId: string,
    previous: string,
    next: string
}

export function isScriptProcessor(config: SmartTriggerProcessorData | SmartTriggerScriptProcessorData): config is SmartTriggerScriptProcessorData {
    return "script" in config;
}

export type SmartTriggerProcessorConfigurationAction =
    ProcessorUpdateAction
    | NewProcessorAction
    | ProcessRepositoryPullAction


const languageRegistry: Record<string, string> = {
    'mysql': "select * from ck_outlets where activeStatus = 'ACTIVE'; ",
    'javascript': `function process(payload,  processorConfiguration, smartTriggerConfiguration) {
    const value = payload.order.value; // getting name from payload
    const input = processorConfiguration.inputs.minimumOrder
    const parent = smartTriggerConfiguraton.inputs.immediateParent
    
    //apply your logics
    
    return payload;
}`,
    'python': `def process(payload, processorConfiguration, smartTriggerConfiguration):
    value = payload['order']['value'] # getting name from payload
    input = processorConfiguration['inputs']['minimumOrder']
    parent = smartTriggerConfiguration['inputs']['immediateParent']
    
    # apply your logics
    
    return payload`
}


export function SmartTriggerProcessorConfigurationModal({
                                                            isOpened,
                                                            onClose, action
                                                        }: SmartTriggerProcessorConfigurationModalProps) {

    const [state, setState] = useState<SmartTriggerProcessorNodeConfiguration>({
        id: uuidv4(),
        type: 'script',
        position: {
            x: 0,
            y: 0
        },
        data: {
            name: "",
            description: "",
            previous: "",
            next: "",
            script: 'mysql',
            implementation: '',
            inputs: [],
            author: ""
        }
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    let smartTriggerRepository = useSmartTriggerRepository();

    let smartTriggerConfiguration = useSmartTriggerConfiguration();

    useEffect(() => {
        setErrors({})
        if (action.type === 'new') {
            setState({...state, data: {...state.data, previous: action.previous, next: action.next}})
        }
        if (action.type === 'pull') {
            const processorFromRepo = smartTriggerRepository.processors.find(processor => processor.id === action.processorRepositoryId)
            if (!processorFromRepo) {
                throw new Error('Could not find processor from repo by id: ' + action.processorRepositoryId);
            }
            setState({
                ...state,
                data: {
                    ...state.data,
                    name: processorFromRepo.name,
                    description: processorFromRepo.description,
                    previous: action.previous,
                    next: action.next,
                    author: processorFromRepo.author,
                    script: processorFromRepo.script,
                    implementation: processorFromRepo.implementation,
                    inputs: processorFromRepo.inputs
                }
            })
        }
        if (action.type === 'update') {
            const node = findProcessorNode(action.processorId, smartTriggerConfiguration.nodes)
            setState({...node})
        }

    }, [action]);

    function onLanguageChange(name: ScriptLanguage) {
        setState({...state, data: {...data, script: name, implementation: languageRegistry[name]}})
    }

    const data = state.data as SmartTriggerScriptProcessorData;

    function validate() {
        const validationErrors = {} as Record<string, string>
        if (StringUtils.isBlank(data.name)) {
            validationErrors['name'] = "Please provide Processor Name";
        }
        if (StringUtils.isBlank(data.description)) {
            validationErrors["description"] = 'Please provide some description about this processor'
        }
        if (StringUtils.isBlank(data.author)) {
            validationErrors['author'] = 'Oops we need the author name'
        }
        return validationErrors;
    }

    function save() {
        if (action.type === 'update') {
            smartTriggerConfiguration.updateNode(state)
        } else {
            smartTriggerConfiguration.addNewProcessor(state)
        }
        onClose()
    }

    function onSave() {
        const validationErrors = validate();
        if (ObjectUtils.isEmpty(validationErrors)) {
            save();
            return;
        }
        setErrors(validationErrors);
    }

    function updateValue(stateToUpdate: string, event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
        let value = event.currentTarget.value;
        if (stateToUpdate === 'name') {
            setState({...state, data: {...state.data, name: value}})
            setErrors(ObjectUtils.deleteKey(errors, 'name'))
        }
        if (stateToUpdate === 'description') {
            setState({...state, data: {...state.data, description: value}})
            setErrors(ObjectUtils.deleteKey(errors, 'description'))
        }
        if (stateToUpdate === 'author') {
            setState({...state, data: {...state.data, author: value}})
            setErrors(ObjectUtils.deleteKey(errors, 'author'))
        }
    }


    return (
        <>
            <Modal className={"smart-trigger-node-configuration-modal"}
                   closeOnClickOutside={true}
                   closeOnEscape={true}
                   withCloseButton={false}
                   overlayOpacity={0.4}
                   size={"80%"}
                   style={{padding: "0px"}}
                // centered={true}
                   padding={0}
                   styles={{
                       body: {backgroundColor: 'white'}
                   }}
                   opened={isOpened}
                   onClose={() => onClose()}
                // title="Introduce yourself!"
            >
                <div className={"processor-modal-parent"}>
                    <div className={"processor-modal-title"}>
                        <h4 className={"processor-modal-title-name"}>CONFIGURE YOUR PROCESSOR</h4>
                    </div>
                    <div className={"modal-parent-div"}>
                        <div className={"modal-script"}>
                            <AceEditor
                                className={"processor_ace_editor"}
                                showGutter={false}
                                width={"100%"}
                                height={"100%"}
                                mode={data.script}
                                enableLiveAutocompletion
                                theme="github"
                                value={data.implementation === '' ? languageRegistry[data.script] : data.implementation}
                                showPrintMargin={false}
                                // onChange={onChange}
                                name="UNIQUE_ID_OF_DIV"
                                editorProps={{$blockScrolling: true}}
                            />
                        </div>
                        <div className={"modal-settings-section"}>
                            <div className={"processor-basic-configuration"}>
                                <div className="form-element">
                                    <TextInput
                                        error={errors['name']}
                                        value={data.name}
                                        onChange={event => updateValue('name', event)}
                                        placeholder={"PROCESSOR NAME"}
                                        label={"NAME"}/>
                                </div>
                                <div className="form-element">
                                    <TextInput value={data.author}
                                               error={errors['author']}
                                               onChange={event => updateValue('author', event)}
                                               placeholder={"Please provide your name"}
                                               label={"OWNER"}/>
                                </div>
                                <div className="form-element">
                                    <Textarea value={data.description}
                                              error={errors['description']}
                                              onChange={event => updateValue('description', event)}
                                              placeholder={"Please mention about this Processor"}
                                              label={"DESCRIPTION"}/>
                                </div>
                            </div>
                            <hr className={"common-divider"}/>
                            <div className={"modal-form-group"}>
                                <label>PROCESSOR SCRIPT LANGUAGE</label>
                                <div className={"processor-language-selection"}>

                                    <LanguageCard language="mysql"
                                                  isSelected={StringUtils.equalIgnoreCase("mysql", data.script)}
                                                  image={require('../../../../assets/img/scripts/languages/sql.png')}
                                                  onClick={onLanguageChange}/>
                                    <LanguageCard language="javascript"
                                                  image={require('../../../../assets/img/scripts/languages/javascript.png')}
                                                  isSelected={StringUtils.equalIgnoreCase("javascript", data.script)}
                                                  onClick={onLanguageChange}/>
                                    <LanguageCard language="python"
                                                  isSelected={StringUtils.equalIgnoreCase("python", data.script)}
                                                  image={require('../../../../assets/img/scripts/languages/python.png')}
                                                  onClick={onLanguageChange}/>
                                </div>
                            </div>
                            <div className={"modal-settings-footer"}>
                                <Button size={"xs"} rightIcon={<Bug/>} variant={"outline"} color={"black"}>
                                    Test
                                </Button>
                                <Button onClick={onSave} size={"xs"} rightIcon={<DeviceFloppy/>} variant={"outline"}
                                        color={"teal"}>
                                    Save
                                </Button>
                                <Button onClick={() => onClose()} size={"xs"} rightIcon={<CircleX/>} variant={"outline"}
                                        color={"red"}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
        ;
}
